// Shared notifications store — backed by the real /notifications API.
//
// This used to be a localStorage mock seeded with fake rows, which meant every
// user saw the same 12 placeholders and nothing real ever arrived. It now reads
// per-user rows from the backend; the exported surface is unchanged, so both
// consumers (the Topbar bell and the Notifications page) work as-is.
//
// State is exposed through `useSyncExternalStore`, so no context/provider is
// needed: any component can call `useNotifications()`.
//
// Mutations are OPTIMISTIC — the UI updates immediately and the request is fired
// in the background, then reconciled. A failed request re-syncs from the server
// rather than leaving the UI lying about what happened.
import { useSyncExternalStore, useEffect } from "react";
import { Bug, CheckCircle2, AlertTriangle, Sparkles, Settings2 } from "lucide-react";
import { api } from "@/api/client";
import { toastError } from "@/lib/toast";
import { formatDate } from "./dates";

// Type → presentation. Icons are React components (never persisted); the API
// only stores the `type` string. Keep in sync with NOTIF_TYPES in
// backend/api/routers/notifications.py.
export const NOTIF_TYPES = {
  bug:    { label: "Bug",    icon: Bug,           tone: "text-rose-500 bg-rose-500/10" },
  run:    { label: "Run",    icon: CheckCircle2,  tone: "text-emerald-500 bg-emerald-500/10" },
  alert:  { label: "Alert",  icon: AlertTriangle, tone: "text-amber-500 bg-amber-500/10" },
  info:   { label: "Info",   icon: Sparkles,      tone: "text-brand-500 bg-brand-500/10" },
  system: { label: "System", icon: Settings2,     tone: "text-slate-500 bg-slate-500/10" },
};

export const NOTIF_TYPE_KEYS = Object.keys(NOTIF_TYPES);

// How often the bell re-checks while the tab is visible.
const POLL_MS = 60_000;

// The list endpoint has no offset/cursor, so this is a hard cap on what the UI
// can hold — exported so the page can say so instead of pretending it's the lot.
export const NOTIF_LIMIT = 100;

// `unread` is the SERVER's total, not a count of the loaded rows: past the cap
// the two disagree and the badge was under-reporting.
let state = { items: [], unread: 0, loading: true, error: false };
const listeners = new Set();
let inflight = null;
let timer = null;
let mounted = 0;

function set(next) {
  state = next;
  listeners.forEach((l) => l());
}

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return state;
}

// --- Loading ---------------------------------------------------------------
// A refresh that STARTED before a local mutation must not undo it. The 60s poll
// would otherwise land with a pre-mutation snapshot and flip a just-read
// notification back to unread under the user's cursor.
let lastMutationAt = 0;

// The bell offers to turn on desktop alerts, so something has to raise them —
// until now the only emitter lived in the tester's extension runner and the
// granted permission did nothing anywhere else. Ids from the previous load are
// remembered so the first fetch (and the one after a sign-in) never replays the
// whole inbox as a burst of alerts.
let seenIds = null;

function announce(items) {
  const previous = seenIds;
  seenIds = new Set(items.map((n) => n.id));
  if (!previous) return;                                  // first load — nothing is new yet
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  for (const n of items) {
    if (n.read || previous.has(n.id)) continue;
    try {
      new Notification(n.title, { body: n.body, tag: n.id });
    } catch (e) {
      break;                                              // blocked/unsupported — don't retry per row
    }
  }
}

export async function refresh() {
  // Collapse concurrent callers (bell + page mount together) into one request.
  if (inflight) return inflight;
  const startedAt = Date.now();
  inflight = (async () => {
    try {
      const res = await api.get("/notifications", { params: { limit: NOTIF_LIMIT } });
      if (lastMutationAt > startedAt) return;   // stale snapshot — a mutation won
      const items = Array.isArray(res?.items) ? res.items : [];
      const unread = Number.isFinite(res?.unread) ? res.unread : items.filter((n) => !n.read).length;
      announce(items);
      set({ items, unread, loading: false, error: false });
    } catch (e) {
      // Signed out or backend unreachable — keep what we have and flag it rather
      // than inventing data.
      set({ ...state, loading: false, error: true });
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

// The bell only needs a number, and the backend ships a one-integer endpoint for
// exactly that. Re-pull the full inbox only when the count actually moved (or
// after a failed load) instead of downloading 100 rows a minute in every tab.
async function poll() {
  try {
    const res = await api.get("/notifications/unread-count");
    const unread = Number(res?.unread) || 0;
    if (state.error || unread !== state.unread) await refresh();
  } catch (e) {
    await refresh();   // count endpoint unreachable — let the list own the error state
  }
}

function startPolling() {
  if (timer) return;
  timer = setInterval(() => {
    if (typeof document !== "undefined" && document.hidden) return; // don't poll a hidden tab
    poll();
  }, POLL_MS);
}

function stopPolling() {
  if (timer) { clearInterval(timer); timer = null; }
}

// A fresh sign-in must not show the previous user's notifications. A 401 fires
// this via the event below; a clean sign-out never 401s, so AuthContext's logout
// calls it directly.
export function reset() {
  // A refresh that started before the reset would land with the previous user's
  // rows — mark it stale the same way a local mutation does. Polling stops too:
  // the session is over, and the next consumer to mount restarts it.
  lastMutationAt = Date.now();
  stopPolling();
  seenIds = null;
  set({ items: [], unread: 0, loading: false, error: false });
}

if (typeof window !== "undefined") {
  window.addEventListener("aiqa:unauthorized", reset);
}

// --- Mutations (optimistic, then reconciled) --------------------------------
// `unread` is adjusted by hand rather than recounted from `items`: past the cap
// the store holds fewer rows than the server, so recounting would lose the rest.
function patch(fn, unread = state.unread) {
  lastMutationAt = Date.now();
  set({ ...state, items: fn(state.items), unread: Math.max(0, unread) });
}

// The optimistic update has already told the user it worked, so a failure has to
// be said out loud — reverting silently is what made deleted rows "come back by
// themselves".
async function reportFailure(e, message) {
  toastError(e?.message || message);
  await refresh();
}

export async function markRead(id) {
  const wasUnread = state.items.some((n) => n.id === id && !n.read);
  patch((items) => items.map((n) => (n.id === id ? { ...n, read: true } : n)),
        state.unread - (wasUnread ? 1 : 0));
  try {
    await api.post(`/notifications/${id}/read`, {});
  } catch (e) {
    await reportFailure(e, "Couldn't mark that notification as read.");
  }
}

export async function markUnread(id) {
  const wasRead = state.items.some((n) => n.id === id && n.read);
  patch((items) => items.map((n) => (n.id === id ? { ...n, read: false } : n)),
        state.unread + (wasRead ? 1 : 0));
  try {
    await api.post(`/notifications/${id}/unread`, {});
  } catch (e) {
    await reportFailure(e, "Couldn't mark that notification as unread.");
  }
}

export async function markAllRead() {
  patch((items) => items.map((n) => (n.read ? n : { ...n, read: true })), 0);
  try {
    await api.post("/notifications/read-all", {});
  } catch (e) {
    await reportFailure(e, "Couldn't mark your notifications as read.");
  }
}

export async function removeMany(ids) {
  const drop = new Set(ids);
  const unreadDropped = state.items.filter((n) => drop.has(n.id) && !n.read).length;
  patch((items) => items.filter((n) => !drop.has(n.id)), state.unread - unreadDropped);
  try {
    await api.post("/notifications/delete", { ids });
  } catch (e) {
    await reportFailure(e, "Couldn't delete those notifications.");
    throw e;   // the caller's confirm dialog stays open so it can be retried
  }
}

export async function clearAll() {
  patch(() => [], 0);
  try {
    await api.del("/notifications");
  } catch (e) {
    await reportFailure(e, "Couldn't clear your notifications.");
    throw e;
  }
}

// Every notifier writes a `link`, and nothing followed it. The stored values are
// inconsistent — the batch reporter builds an absolute URL from `app_base_url`
// while the in-app notifiers write a bare path — so pick the navigator that can
// actually handle what we were given instead of feeding the router a full URL.
export function openNotificationLink(link, navigate) {
  if (!link) return;
  if (/^https?:\/\//i.test(link)) window.location.assign(link);
  else navigate(link);
}

// --- Hooks -----------------------------------------------------------------
// Returns the notification array (unchanged shape: {id,type,title,body,createdAt,read}).
// Mounting any consumer loads the list once and starts the shared poll; polling
// stops when the last consumer unmounts.
export function useNotifications() {
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    mounted += 1;
    refresh();
    startPolling();
    return () => {
      mounted -= 1;
      if (mounted <= 0) stopPolling();
    };
  }, []);
  return snap.items;
}

// For consumers that want the loading/error state too.
export function useNotificationsState() {
  return useSyncExternalStore(subscribe, getSnapshot);
}

// --- Formatting helpers ----------------------------------------------------
export function timeAgo(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 4) return `${w}w ago`;
  return formatDate(iso, "");
}

// Kept as a re-export so notification callers don't all have to change import,
// while the format itself is the product-wide one.
export { formatDateTime as fmtDateTime } from "./dates";
